import { FC, useState, useEffect, useMemo, useRef } from 'react';
import { Formik } from 'formik';
import { Box, Modal, Typography, Accordion, AccordionSummary, AccordionDetails, Alert } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Form, useNavigate } from 'react-router-dom';

import CustomInput from '../../../../../common/CustomInput';
import CustomButton from '../../../../../common/CustomButton';
import CustomCountryMultiselect from '../../../../../common/CustomCountryMultiselect';
import {
  useCreateTripMutation,
  useCreateTripFromIdeaMutation,
  useUpdateTripMutation,
  useDeleteTripMutation,
  useSaveFlightToTripMutation,
  useSavePlaceToTripMutation,
  useGetDestinationCountriesQuery,
} from '../../../../../store/services/tripsApi';
import type { FlightToIdeaPayload } from '../../../../../types/flight.types';
import type { PlaceToIdeaPayload } from '../../../../../types/place.types';
import { validatePlaceStopWithinTrip } from '../../../../../utils/placeToIdeaPayload';
import { getApiErrorMessage } from '../../../../../utils/apiErrorMessage';
import { useGetTravelBuddiesQuery } from '../../../../../store/services/userContactsApi';
import { useGetIdeaQuery, useLazyGetIdeaStopQuery } from '../../../../../store/services/ideasApi';
import {
  DestinationCountry,
  CreateTripPayload,
  CreateTripFromIdeaBody,
  UpdateTripPayload,
  TravelBuddy,
  Trip,
} from '../../../../../types/trip.types';

import styles from './CreateTripModal.module.scss';
import CustomReactDatePicker from '../../../../../common/CustomReactDatePicker';
import TravelBuddiesSection from '../TravelBuddiesSection';
import AddTravelBuddiesModal from '../AddTravelBuddiesModal';
import { getTripDateRangeFromEmbedStops } from '../../../../../utils/ideaStopsTripDateRange';
import {
  collectIdeaStopsMissingTripFields,
  parseTripMutationApiError,
  TRIP_CONVERSION_MISSING_FIELD_LABELS,
} from '../../../../../utils/sendIdeaStopToTripPayload';
import type { IdeaStop, IdeaStopType } from '../../../../../types/ideaStop.types';

type FormikValues = {
  name: string;
  start_date: string;
  end_date: string;
};

type CreateTripModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess?: (trip?: Trip) => void;
  isEditMode?: boolean;
  tripData?: Trip;
  sourceIdeaId?: number;
  initialTripName?: string;
  initialStartDate?: string;
  initialEndDate?: string;
  /** When set, trip is only finalized after the flight is attached to the trip. */
  pendingFlightToTripBody?: FlightToIdeaPayload | null;
  /** When set, trip is only finalized after the place stop is attached to the trip. */
  pendingPlaceToTripBody?: PlaceToIdeaPayload | null;
  onDiscoverFlowComplete?: () => void;
};

const CreateTripModal: FC<CreateTripModalProps> = ({
  open,
  onClose,
  onSuccess,
  isEditMode = false,
  tripData,
  sourceIdeaId,
  initialTripName,
  initialStartDate,
  initialEndDate,
  pendingFlightToTripBody = null,
  pendingPlaceToTripBody = null,
  onDiscoverFlowComplete,
}) => {
  const navigate = useNavigate();
  const [createTrip, { isLoading: isCreating }] = useCreateTripMutation();
  const [createTripFromIdea, { isLoading: isCreatingFromIdea }] = useCreateTripFromIdeaMutation();
  const [updateTrip, { isLoading: isUpdating }] = useUpdateTripMutation();
  const [deleteTrip] = useDeleteTripMutation();
  const [saveFlightToTrip, { isLoading: isAttachingDiscoverFlight }] = useSaveFlightToTripMutation();
  const [savePlaceToTrip, { isLoading: isAttachingDiscoverPlace }] = useSavePlaceToTripMutation();
  const { data: countries } = useGetDestinationCountriesQuery({});
  const { data: allTravelBuddies } = useGetTravelBuddiesQuery();
  const { data: sourceIdeaDetail, isFetching: isFetchingSourceIdea } = useGetIdeaQuery(
    sourceIdeaId != null ? String(sourceIdeaId) : '',
    {
      skip: !open || sourceIdeaId == null,
      refetchOnMountOrArgChange: true,
    }
  );

  const embedIdeaStops = sourceIdeaDetail?.idea_stops ?? [];

  const isLoading = isCreating || isCreatingFromIdea || isUpdating;
  const isSubmitting = isLoading || isAttachingDiscoverFlight || isAttachingDiscoverPlace;
  const isFromIdeaFlow = Boolean(sourceIdeaId) && !isEditMode;
  const isDiscoverFlightFlow = Boolean(pendingFlightToTripBody) && !isEditMode && sourceIdeaId == null;
  const isDiscoverPlaceFlow = Boolean(pendingPlaceToTripBody) && !isEditMode && sourceIdeaId == null;
  const isDiscoverAttachFlow = isDiscoverFlightFlow || isDiscoverPlaceFlow;

  const embedStopIdsKey = useMemo(() => embedIdeaStops.map(s => s.id).join(','), [embedIdeaStops]);

  const [fetchIdeaStop] = useLazyGetIdeaStopQuery();
  const [fullIdeaStopsForTripValidation, setFullIdeaStopsForTripValidation] = useState<IdeaStop[] | null>(null);
  const [isResolvingIdeaStopsForTrip, setIsResolvingIdeaStopsForTrip] = useState(false);
  const tripStopResolveGen = useRef(0);

  useEffect(() => {
    if (!open || !isFromIdeaFlow || embedIdeaStops.length === 0) {
      setFullIdeaStopsForTripValidation(null);
      setIsResolvingIdeaStopsForTrip(false);
      return;
    }

    const gen = ++tripStopResolveGen.current;
    setIsResolvingIdeaStopsForTrip(true);
    setFullIdeaStopsForTripValidation(null);

    void Promise.all(embedIdeaStops.map(s => fetchIdeaStop(String(s.id)).unwrap()))
      .then(stops => {
        if (tripStopResolveGen.current !== gen) return;
        setFullIdeaStopsForTripValidation(stops);
      })
      .catch(() => {
        if (tripStopResolveGen.current !== gen) return;
        setFullIdeaStopsForTripValidation(null);
      })
      .finally(() => {
        if (tripStopResolveGen.current !== gen) return;
        setIsResolvingIdeaStopsForTrip(false);
      });
  }, [open, isFromIdeaFlow, embedStopIdsKey, fetchIdeaStop]);

  const [selectedCountries, setSelectedCountries] = useState<DestinationCountry[]>([]);
  const [selectedBuddies, setSelectedBuddies] = useState<TravelBuddy[]>([]);
  const [resErrors, setResErrors] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showAddBuddiesModal, setShowAddBuddiesModal] = useState(false);
  const [discoverDraftTripId, setDiscoverDraftTripId] = useState<number | null>(null);

  useEffect(() => {
    if (!open) {
      setDiscoverDraftTripId(null);
    }
  }, [open]);

  const tripDatesFromStops = useMemo(() => {
    if (!isFromIdeaFlow || embedIdeaStops.length === 0) return null;
    return getTripDateRangeFromEmbedStops(embedIdeaStops);
  }, [isFromIdeaFlow, embedIdeaStops]);

  const ideaStopsTripFieldGaps = useMemo(() => {
    if (!isFromIdeaFlow || !fullIdeaStopsForTripValidation || fullIdeaStopsForTripValidation.length === 0) {
      return [];
    }
    return collectIdeaStopsMissingTripFields(
      fullIdeaStopsForTripValidation as Array<
        { id: number; title?: string; stop_type: IdeaStopType } & Partial<IdeaStop>
      >
    );
  }, [isFromIdeaFlow, fullIdeaStopsForTripValidation]);

  const initialValues: FormikValues = useMemo(() => {
    if (isEditMode && tripData) {
      return {
        name: tripData.name,
        start_date: tripData.start_date,
        end_date: tripData.end_date,
      };
    }
    return {
      name: initialTripName?.trim() ?? '',
      start_date: tripDatesFromStops?.startYmd ?? initialStartDate?.trim() ?? '',
      end_date: tripDatesFromStops?.endYmd ?? initialEndDate?.trim() ?? '',
    };
  }, [isEditMode, tripData, initialTripName, initialStartDate, initialEndDate, tripDatesFromStops]);

  const formikMountKey = useMemo(() => {
    if (!isFromIdeaFlow || !open) return 'trip-form';
    return `from-idea-${sourceIdeaId}-${embedIdeaStops.length}-${tripDatesFromStops?.startYmd ?? 'none'}-${tripDatesFromStops?.endYmd ?? 'none'}`;
  }, [isFromIdeaFlow, open, sourceIdeaId, embedIdeaStops.length, tripDatesFromStops]);

  useEffect(() => {
    if (isEditMode && tripData && countries && allTravelBuddies) {
      const tripCountries = countries.filter(country =>
        tripData.destination_countries.some(tc => tc.id === country.id)
      );
      setSelectedCountries(tripCountries);

      if (tripData.buddies && tripData.buddies.length > 0) {
        //@ts-expect-error
        const tripBuddies = allTravelBuddies.filter(buddy => tripData.buddies.includes(buddy.id));
        setSelectedBuddies(tripBuddies);
      } else {
        setSelectedBuddies([]);
      }
    } else if (!isEditMode) {
      setSelectedCountries([]);
      setSelectedBuddies([]);
    }
  }, [isEditMode, tripData, countries, allTravelBuddies]);

  const finishDiscoverFlightFlow = async (trip: Trip) => {
    setIsSuccess(true);
    setResErrors(null);
    setDiscoverDraftTripId(null);
    onDiscoverFlowComplete?.();
    onSuccess?.(trip);
    onClose();
    setIsSuccess(false);
    setSelectedCountries([]);
    setSelectedBuddies([]);
  };

  const handleSubmit = async (values: FormikValues) => {
    const tripName = values.name.trim();

    try {
      let createdOrUpdated: Trip | undefined;

      if (isDiscoverAttachFlow) {
        if (discoverDraftTripId != null) {
          createdOrUpdated = await updateTrip({
            id: discoverDraftTripId,
            name: tripName,
            destination_countries: selectedCountries.map(country => country.id),
            start_date: values.start_date,
            end_date: values.end_date,
            buddies: selectedBuddies.map(buddy => buddy.id),
          }).unwrap();
        } else {
          const payload: CreateTripPayload = {
            name: tripName,
            destination_countries: selectedCountries.map(country => country.id),
            start_date: values.start_date,
            end_date: values.end_date,
            buddies: selectedBuddies.map(buddy => buddy.id),
          };
          createdOrUpdated = await createTrip(payload).unwrap();
          setDiscoverDraftTripId(createdOrUpdated.id);
        }

        const tripId = createdOrUpdated.id;

        if (isDiscoverPlaceFlow && pendingPlaceToTripBody) {
          const stopStart = pendingPlaceToTripBody.start_date_time?.trim() ?? '';
          const stopEnd = pendingPlaceToTripBody.end_date_time?.trim() ?? '';
          if (!stopStart || !stopEnd) {
            setResErrors('Stop dates are missing. Go back to Discover and set stop dates again.');
            return;
          }
          const rangeError = validatePlaceStopWithinTrip(values.start_date, values.end_date, stopStart, stopEnd);
          if (rangeError) {
            setDiscoverDraftTripId(tripId);
            setResErrors(rangeError);
            return;
          }
          try {
            await savePlaceToTrip({ tripId, body: pendingPlaceToTripBody }).unwrap();
          } catch (attachError: unknown) {
            setDiscoverDraftTripId(tripId);
            setResErrors(
              getApiErrorMessage(
                attachError,
                'Could not add this place to the trip. Adjust the trip dates and try again.',
                { firstOnly: true }
              )
            );
            return;
          }
        } else if (isDiscoverFlightFlow && pendingFlightToTripBody) {
          try {
            await saveFlightToTrip({ tripId, body: pendingFlightToTripBody }).unwrap();
          } catch (attachError: unknown) {
            setDiscoverDraftTripId(tripId);
            setResErrors(
              getApiErrorMessage(
                attachError,
                'Could not add this flight to the trip. Adjust the trip dates and try again.',
                { firstOnly: true }
              )
            );
            return;
          }
        }

        await finishDiscoverFlightFlow(createdOrUpdated);
        return;
      }

      if (isEditMode && tripData) {
        const payload: UpdateTripPayload = {
          id: tripData.id,
          name: tripName,
          destination_countries: selectedCountries.map(country => country.id),
          start_date: values.start_date,
          end_date: values.end_date,
          buddies: selectedBuddies.map(buddy => buddy.id),
        };

        console.log('Updating trip with payload:', payload);
        createdOrUpdated = await updateTrip(payload).unwrap();
      } else if (sourceIdeaId != null) {
        if (isResolvingIdeaStopsForTrip) {
          return;
        }
        const stopsForGaps = fullIdeaStopsForTripValidation ?? [];
        const gaps = collectIdeaStopsMissingTripFields(
          stopsForGaps as Array<{ id: number; title?: string; stop_type: IdeaStopType } & Partial<IdeaStop>>
        );
        if (gaps.length > 0) {
          return;
        }

        const body: CreateTripFromIdeaBody = {
          name: tripName,
          destination_countries: selectedCountries.map(country => country.id),
          buddies: selectedBuddies.map(buddy => buddy.id),
        };
        if (values.start_date.trim()) {
          body.start_date = values.start_date;
        }
        if (values.end_date.trim()) {
          body.end_date = values.end_date;
        }

        createdOrUpdated = await createTripFromIdea({ ideaId: sourceIdeaId, body }).unwrap();
      } else {
        const payload: CreateTripPayload = {
          name: tripName,
          destination_countries: selectedCountries.map(country => country.id),
          start_date: values.start_date,
          end_date: values.end_date,
          buddies: selectedBuddies.map(buddy => buddy.id),
        };

        console.log('Creating trip with payload:', payload);
        createdOrUpdated = await createTrip(payload).unwrap();
      }

      setIsSuccess(true);
      setResErrors(null);
      onSuccess?.(createdOrUpdated);

      onClose();
      setIsSuccess(false);
      setSelectedCountries([]);
      setSelectedBuddies([]);
    } catch (error: unknown) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} trip:`, error);
      setResErrors(parseTripMutationApiError(error, `Failed to ${isEditMode ? 'update' : 'create'} trip`));
    }
  };

  const formikValidate = (values: FormikValues) => {
    const errors: Partial<FormikValues> = {};

    if (!values.name.trim()) {
      errors.name = 'Trip name is required';
    }

    if (isFromIdeaFlow) {
      const hasStart = Boolean(values.start_date?.trim());
      const hasEnd = Boolean(values.end_date?.trim());
      if (hasStart !== hasEnd) {
        errors.start_date = 'Provide both start and end dates, or leave both empty';
        errors.end_date = 'Provide both start and end dates, or leave both empty';
      }
      if (values.start_date && values.end_date && values.start_date > values.end_date) {
        errors.end_date = 'End date must be after start date';
      }
    } else {
      if (!values.start_date) {
        errors.start_date = 'Start date is required';
      }

      if (!values.end_date) {
        errors.end_date = 'End date is required';
      }

      if (values.start_date && values.end_date && values.start_date > values.end_date) {
        errors.end_date = 'End date must be after start date';
      }
    }

    return errors;
  };

  const handleClose = async () => {
    if (discoverDraftTripId != null) {
      try {
        await deleteTrip(String(discoverDraftTripId)).unwrap();
      } catch (error) {
        console.error('Failed to remove draft trip after Discover save was cancelled:', error);
      }
    }
    setDiscoverDraftTripId(null);
    onClose();
    setSelectedCountries([]);
    setSelectedBuddies([]);
    setResErrors(null);
    setIsSuccess(false);
    setShowAddBuddiesModal(false);
  };

  const handleAddBuddiesOpen = () => {
    setShowAddBuddiesModal(true);
  };

  const handleAddBuddiesClose = () => {
    setShowAddBuddiesModal(false);
  };

  const handleBuddiesChange = (buddies: TravelBuddy[]) => {
    setSelectedBuddies(buddies);
  };

  const handleRemoveBuddy = (buddyId: number) => {
    setSelectedBuddies(prev => prev.filter(buddy => buddy.id !== buddyId));
  };

  const getModalTitle = () => {
    return isEditMode ? 'Edit Trip' : 'Create New Trip';
  };

  const getSubmitButtonText = () => {
    if (isAttachingDiscoverFlight) {
      return 'Adding flight…';
    }
    if (isAttachingDiscoverPlace) {
      return 'Adding place…';
    }
    if (isLoading) {
      return isEditMode ? 'Updating...' : discoverDraftTripId != null ? 'Updating...' : 'Creating...';
    }
    if (isDiscoverPlaceFlow) {
      return discoverDraftTripId != null ? 'Update & add place' : 'Create Trip';
    }
    if (isDiscoverFlightFlow) {
      return discoverDraftTripId != null ? 'Update & add flight' : 'Create Trip';
    }
    return isEditMode ? 'Update Trip' : 'Create Trip';
  };

  const getSuccessMessage = () => {
    if (isEditMode) {
      return 'Trip updated successfully!';
    }
    if (isFromIdeaFlow) {
      return 'Trip created from your idea!';
    }
    return 'Trip created successfully!';
  };

  return (
    <>
      <Modal
        open={open}
        onClose={() => void handleClose()}
        aria-labelledby={isEditMode ? 'edit-trip-modal' : 'create-trip-modal'}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(34, 43, 58, 0.2)',
          },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            maxWidth: '600px',
            minWidth: 375,
            width: '100%',
            bgcolor: 'background.paper',
            borderRadius: '20px',
            p: '32px 0',
            boxShadow: 'none',
            maxHeight: '90vh',
            overflow: 'hidden',
            display: showAddBuddiesModal ? 'none' : 'block',
            '@media (max-width: 900px)': {
              p: '16px 0',
            },
          }}
        >
          <Box sx={{ position: 'absolute', top: 16, right: 20, cursor: 'pointer' }}>
            <CloseIcon
              sx={{ width: '30px', height: '30px', color: '#5E6A81' }}
              onClick={() => void handleClose()}
            />
          </Box>

          <Box className={styles.modalContent}>
            <Typography
              variant="h5Bold"
              sx={{ textAlign: 'center', mb: 3, color: '#222B3A' }}
            >
              {getModalTitle()}
            </Typography>

            {isFromIdeaFlow && (isFetchingSourceIdea || isResolvingIdeaStopsForTrip) ? (
              <Typography
                variant="h11"
                sx={{ textAlign: 'center', color: '#5E6A81', mb: 2, px: 2 }}
              >
                {isFetchingSourceIdea
                  ? 'Loading idea stops to suggest trip dates…'
                  : 'Checking idea stops for trip requirements…'}
              </Typography>
            ) : null}

            {isFromIdeaFlow &&
            !isFetchingSourceIdea &&
            !isResolvingIdeaStopsForTrip &&
            ideaStopsTripFieldGaps.length > 0 ? (
              <Alert
                severity="warning"
                sx={{ mb: 2, mx: 2, textAlign: 'left' }}
              >
                <Typography
                  variant="h11"
                  sx={{ mb: 1, fontWeight: 600 }}
                >
                  Some stops in this idea do not have the start/end times (or other details) needed to create a trip.
                  Open the idea, edit each stop below, fill the missing fields, save, then create the trip again.
                </Typography>
                <Box
                  component="ul"
                  sx={{ m: 0, pl: 2.5, mb: 2 }}
                >
                  {ideaStopsTripFieldGaps.map(row => (
                    <Typography
                      key={row.id}
                      component="li"
                      variant="h11"
                    >
                      <strong>{row.title}</strong> ({row.stop_type}):{' '}
                      {row.missingKeys.map(k => TRIP_CONVERSION_MISSING_FIELD_LABELS[k] ?? k).join(', ')}
                    </Typography>
                  ))}
                </Box>
                <CustomButton
                  variant="contained"
                  btnType="button"
                  sx={{ padding: '10px 26px' }}
                  onClick={() => {
                    handleClose();
                    navigate(`/ideas/${sourceIdeaId}`);
                  }}
                >
                  Open idea
                </CustomButton>
              </Alert>
            ) : null}

            <Formik
              key={formikMountKey}
              initialValues={initialValues}
              validate={formikValidate}
              onSubmit={handleSubmit}
              enableReinitialize={true}
            >
              {({ values, handleChange, handleSubmit, errors, touched }) => (
                <Form
                  className={styles.tripForm}
                  onSubmit={handleSubmit}
                >
                  <Accordion
                    defaultExpanded
                    className={styles.accordion}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      className={styles.accordionSummary}
                    >
                      <Typography
                        variant="h9Bold"
                        sx={{ color: '#222B3A', marginBottom: '24px' }}
                      >
                        Trip Information
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box className={styles.formSection}>
                        <CustomInput
                          inputName="name"
                          inputLabel="Trip Name"
                          value={values.name}
                          onChange={handleChange}
                          error={touched.name && errors.name ? errors.name : ''}
                        />

                        <CustomCountryMultiselect
                          options={countries || []}
                          value={selectedCountries}
                          onChange={setSelectedCountries}
                          inputLabel="Destination Countries"
                          placeholder="Enter country name"
                        />

                        <Box className={styles.dateRow}>
                          <CustomReactDatePicker
                            name="start_date"
                            inputLabel="Start Date"
                            placeholder="Select start date"
                            value={values.start_date}
                            onChange={handleChange}
                            error={touched.start_date && !!errors.start_date}
                            helperText={touched.start_date && errors.start_date ? errors.start_date : ''}
                            iconPosition="right"
                            min={isFromIdeaFlow ? undefined : new Date().toISOString().split('T')[0]}
                          />

                          <CustomReactDatePicker
                            name="end_date"
                            inputLabel="End Date"
                            placeholder="Select end date"
                            value={values.end_date}
                            onChange={handleChange}
                            error={touched.end_date && !!errors.end_date}
                            helperText={touched.end_date && errors.end_date ? errors.end_date : ''}
                            iconPosition="right"
                            min={
                              values.start_date ||
                              (isFromIdeaFlow ? tripDatesFromStops?.startYmd : undefined) ||
                              (!isFromIdeaFlow ? new Date().toISOString().split('T')[0] : undefined)
                            }
                          />
                        </Box>
                      </Box>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion className={styles.accordion}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      className={styles.accordionSummary}
                    >
                      <Typography
                        variant="h9Bold"
                        sx={{ color: '#222B3A' }}
                      >
                        Travel Buddies
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TravelBuddiesSection
                        selectedBuddies={selectedBuddies}
                        onAddBuddies={handleAddBuddiesOpen}
                        onRemoveBuddy={handleRemoveBuddy}
                      />
                    </AccordionDetails>
                  </Accordion>

                  <Box className={styles.modalActions}>
                    <CustomButton
                      btnType="button"
                      variant="outlined"
                      onClick={() => void handleClose()}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </CustomButton>
                    <CustomButton
                      btnType="submit"
                      variant="contained"
                      loading={isSubmitting}
                      disabled={
                        isSubmitting ||
                        !values.name.trim() ||
                        selectedCountries.length === 0 ||
                        (!isFromIdeaFlow && (!values.start_date || !values.end_date)) ||
                        (isFromIdeaFlow && (isFetchingSourceIdea || isResolvingIdeaStopsForTrip)) ||
                        (isFromIdeaFlow && ideaStopsTripFieldGaps.length > 0)
                      }
                    >
                      {getSubmitButtonText()}
                    </CustomButton>
                  </Box>

                  {isSuccess && (
                    <Typography
                      variant="h12"
                      sx={{ color: '#208B64', textAlign: 'center', mt: 2 }}
                    >
                      {getSuccessMessage()}
                    </Typography>
                  )}

                  {resErrors ? (
                    <Alert
                      severity="error"
                      onClose={() => setResErrors(null)}
                      sx={{ mt: 2 }}
                    >
                      {resErrors}
                    </Alert>
                  ) : null}
                </Form>
              )}
            </Formik>
          </Box>
        </Box>
      </Modal>

      <AddTravelBuddiesModal
        open={showAddBuddiesModal}
        onClose={handleAddBuddiesClose}
        selectedBuddies={selectedBuddies}
        onBuddiesChange={handleBuddiesChange}
      />
    </>
  );
};

export default CreateTripModal;
